import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

/**
 * Navegación "Anterior / Siguiente" del ERP.
 *  - Recorre las vistas visitadas dentro del ERP (ruta + filtros en la URL + posición de desplazamiento).
 *  - En procesos de varios pasos (useStepNav) recorre los pasos de la actividad.
 *  - Protege cambios sin guardar: Guardar / Descartar / Cancelar. Nunca emite ni confirma operaciones:
 *    "Guardar" solo usa el botón de guardar del formulario y, si falla, se queda en la vista.
 *  - Los permisos los sigue aplicando cada ruta (ProtectedRoute): las flechas solo navegan.
 */
const NavCtx = createContext(null);
const KEY = "agro_nav_v1";
const EXCLUIDAS = ["/", "/login", "/forgot-password", "/reset-password"];
const urlOf = (l) => `${l.pathname}${l.search}${l.hash}`;
const SAVE_RX = /^(guardar|actualizar|crear|registrar|agregar|aplicar cambios)/i;
const NO_SAVE_RX = /(emitir|confirmar|anular|eliminar|borrar|pagar|autorizar|importar|enviar)/i;

function leer() {
  try { return JSON.parse(sessionStorage.getItem(KEY)) || { stack: [], index: -1 }; }
  catch { return { stack: [], index: -1 }; }
}

export function NavigationProvider({ children, userId }) {
  const loc = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const [hist, setHist] = useState(() => {
    const h = leer();
    // mientras se carga la sesión (userId vacío) se conserva el historial guardado
    return !userId || h.user === userId ? h : { stack: [], index: -1, user: userId };
  });
  const pending = useRef(null);            // índice destino de una navegación hecha con las flechas
  const scrollDestino = useRef(0);
  const [steps, setSteps] = useState(null);
  const guards = useRef(new Map());
  const autoDirty = useRef(null);          // contenedor (formulario / diálogo) con cambios sin guardar
  const [prompt, setPrompt] = useState(null);

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify({ ...hist, user: userId || hist.user })); } catch { /* sin almacenamiento */ }
  }, [hist, userId]);
  // Otro usuario inició sesión en esta pestaña: su historial empieza vacío
  useEffect(() => {
    if (!userId) return;
    setHist((h) => (!h.user || h.user === userId ? { ...h, user: userId } : { stack: [], index: -1, user: userId }));
  }, [userId]);

  // Registro de vistas visitadas
  useEffect(() => {
    if (EXCLUIDAS.includes(loc.pathname)) return;
    const url = urlOf(loc);
    autoDirty.current = null;
    setHist((h) => {
      const stack = [...h.stack];
      let index = h.index;
      if (pending.current != null) {
        index = pending.current;
        pending.current = null;
        stack[index] = { ...stack[index], url };
      } else if (navType === "REPLACE" && index >= 0) {
        stack[index] = { ...stack[index], url };          // filtros / pestañas que reemplazan la URL
      } else if (navType === "POP" && stack[index - 1]?.url === url) {
        index -= 1;                                        // atrás del navegador
      } else if (navType === "POP" && stack[index + 1]?.url === url) {
        index += 1;
      } else if (stack[index]?.url !== url) {
        stack.splice(index + 1);
        stack.push({ url, scroll: 0 });
        index = stack.length - 1;
        if (stack.length > 100) { stack.shift(); index -= 1; }
      }
      return { ...h, stack, index };
    });
    if (scrollDestino.current) {                          // vuelve a la posición en la que se dejó la vista
      const y = scrollDestino.current;
      scrollDestino.current = 0;
      setTimeout(() => window.scrollTo(0, y), 80);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key, loc.pathname, loc.search, loc.hash]);

  // Detección automática de cambios sin guardar en formularios y diálogos
  useEffect(() => {
    const onInput = (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement) || !t.matches("input, textarea, select")) return;
      if (t.type === "search" || t.closest("[data-nav-ignore]")) return;
      const scope = t.closest("form, [role=dialog], [data-dirty-scope], .fixed.inset-0");
      if (scope) autoDirty.current = scope;
    };
    // un guardado exitoso (aviso de éxito) limpia la marca
    const obs = new MutationObserver((muts) => {
      for (const m of muts) for (const n of m.addedNodes) {
        if (n instanceof HTMLElement && (n.matches?.("[data-sonner-toast][data-type=success]")
            || n.querySelector?.("[data-sonner-toast][data-type=success]"))) autoDirty.current = null;
      }
    });
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    obs.observe(document.body, { childList: true, subtree: true });
    const beforeUnload = (e) => { if (hayCambios()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
      obs.disconnect();
      window.removeEventListener("beforeunload", beforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardActivo = () => [...guards.current.values()].find((g) => g.isDirty());
  const hayCambios = () => Boolean(guardActivo() || (autoDirty.current && autoDirty.current.isConnected));

  const irA = useCallback((index) => {
    setHist((h) => {
      const stack = [...h.stack];
      if (stack[h.index]) stack[h.index] = { ...stack[h.index], scroll: window.scrollY };
      return { ...h, stack };
    });
    pending.current = index;
    scrollDestino.current = hist.stack[index].scroll || 0;
    autoDirty.current = null;
    navigate(hist.stack[index].url);
  }, [hist, navigate]);

  const ejecutar = useCallback((accion) => {
    if (!hayCambios()) { accion(); return; }
    setPrompt({ accion });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canPrev = steps ? steps.canPrev : hist.index > 0;
  const canNext = steps ? steps.canNext : hist.index >= 0 && hist.index < hist.stack.length - 1;
  const prev = useCallback(() => {
    if (!canPrev) return;
    if (steps) { steps.prev(); return; }
    ejecutar(() => irA(hist.index - 1));
  }, [canPrev, steps, ejecutar, irA, hist.index]);
  const next = useCallback(() => {
    if (!canNext) return;
    if (steps) { steps.next(); return; }
    ejecutar(() => irA(hist.index + 1));
  }, [canNext, steps, ejecutar, irA, hist.index]);

  // Teclado: Alt + ← / Alt + →
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Guardar desde el aviso: guard explícito o el botón de guardar del formulario / diálogo con cambios
  const guardar = async () => {
    const g = guardActivo();
    if (g?.save) {
      const ok = await g.save();
      if (ok === false) throw new Error("No se pudo guardar.");
      return true;
    }
    if (g) throw new Error(g.sinGuardar || "Esta pantalla no se guarda desde aquí: descarta los cambios o cancela.");
    const scope = autoDirty.current;
    const btn = scope && [...scope.querySelectorAll("button")].find((b) => !b.disabled
      && (b.type === "submit" || SAVE_RX.test((b.textContent || "").trim()) || /save|guardar|submit/i.test(b.dataset.testid || ""))
      && !NO_SAVE_RX.test(b.textContent || ""));
    if (!btn) throw new Error("Este formulario no tiene un botón de guardar: descarta los cambios o cancela.");
    const form = btn.form || scope.closest?.("form") || (scope.matches?.("form") ? scope : null);
    if (form && !form.checkValidity()) {
      form.reportValidity();
      throw new Error("Completa los campos obligatorios del formulario.");
    }
    const antes = new Set([...document.querySelectorAll("[data-sonner-toast]")]);
    btn.click();
    for (let i = 0; i < 80; i += 1) {
      await new Promise((r) => setTimeout(r, 100));
      const nuevos = [...document.querySelectorAll("[data-sonner-toast]")].filter((t) => !antes.has(t));
      const err = nuevos.find((t) => t.dataset.type === "error");
      if (err) throw new Error(err.textContent || "No se pudo guardar");
      if (!scope.isConnected || nuevos.some((t) => t.dataset.type === "success") || !autoDirty.current) return true;
    }
    throw new Error("No se confirmó el guardado. Revisa el formulario.");
  };

  const value = useMemo(() => ({
    canPrev, canNext, prev, next, steps, hist,
    setSteps, guards, hayCambios,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [canPrev, canNext, prev, next, steps, hist]);

  return (
    <NavCtx.Provider value={value}>
      {children}
      {prompt && <UnsavedDialog
        onCancel={() => setPrompt(null)}
        onDiscard={() => { guards.current.forEach((g) => g.discard?.()); autoDirty.current = null; const a = prompt.accion; setPrompt(null); a(); }}
        onSave={async () => { await guardar(); autoDirty.current = null; const a = prompt.accion; setPrompt(null); a(); }} />}
    </NavCtx.Provider>
  );
}

function UnsavedDialog({ onCancel, onDiscard, onSave }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector("button")?.focus();
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const save = async () => {
    setBusy(true); setErr("");
    try { await onSave(); } catch (e) { setErr(e.message || "No se pudo guardar"); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-nav-ignore>
      <div ref={ref} role="alertdialog" aria-modal="true" aria-labelledby="nav-unsaved-title" data-testid="nav-unsaved-dialog"
           className="bg-card border border-border rounded-lg w-full max-w-md p-5 shadow-2xl">
        <h2 id="nav-unsaved-title" className="font-heading text-lg">Hay cambios sin guardar</h2>
        <p className="text-sm text-text-secondary mt-1">¿Qué deseas hacer antes de salir de esta vista?</p>
        {err && <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="nav-save-error">
          No se guardó: {err} Permaneces en la vista.</div>}
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          <button onClick={onCancel} data-testid="nav-cancel" className="px-3 py-2 text-sm border border-border rounded-md">Cancelar</button>
          <button onClick={onDiscard} data-testid="nav-discard" className="px-3 py-2 text-sm border border-red-500/40 text-red-300 rounded-md">Descartar cambios</button>
          <button onClick={save} disabled={busy} data-testid="nav-save"
            className="px-4 py-2 text-sm bg-agro-green text-black font-semibold rounded-md disabled:opacity-50">{busy ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

export const useNav = () => useContext(NavCtx);

/** Proceso de varios pasos: mientras el componente esté montado, las flechas recorren sus pasos. */
export function useStepNav(cfg) {
  const nav = useNav();
  const setSteps = nav?.setSteps;
  const { enabled = true, index, total, canPrev, canNext, prev, next, label } = cfg || {};
  useEffect(() => {
    if (!setSteps) return undefined;
    if (!enabled) { setSteps(null); return undefined; }
    setSteps({ index, total, canPrev, canNext, prev, next, label });
    return () => setSteps(null);
  }, [setSteps, enabled, index, total, canPrev, canNext, prev, next, label]);
}

/** Cambios sin guardar declarados por la página (más precisos que la detección automática). */
export function useNavGuard(id, { isDirty, save, discard, sinGuardar }) {
  const guards = useNav()?.guards;
  useEffect(() => {
    if (!guards) return undefined;
    const map = guards.current;
    map.set(id, { isDirty, save, discard, sinGuardar });
    return () => map.delete(id);
  }, [guards, id, isDirty, save, discard, sinGuardar]);
}

/** Estado de filtros / búsqueda / página que se conserva al volver a la vista con las flechas. */
export function usePageState(key, inicial) {
  const k = `agro_view:${window.location.pathname}:${key}`;
  const [v, setV] = useState(() => {
    try { const s = sessionStorage.getItem(k); return s != null ? JSON.parse(s) : inicial; } catch { return inicial; }
  });
  useEffect(() => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* sin almacenamiento */ } }, [k, v]);
  return [v, setV];
}
